import {useReducedMotion} from "framer-motion";
import type {CopyControl} from "home/components/shareLink";
import {peelFaceAfterCopy, type PeelTagFace} from "./peelTagFlip";
import {type ReactNode, useEffect, useId, useRef, useState} from "react";
import {useTheme} from "styled-components";
import {useMessages} from "i18n";
import {CouponIcon, GiftIcon} from "download/recipient/icons";
import {ClosableDialog} from "components/ClosableDialog";
import {peelScrimShown, peelScrimTucks, peelTagCollapsed, peelTagOpensInDialog, resolvePeelTag, setPeelTagDismissed, visiblePull, type PeelCandidate} from "./peelTagSlot";
import {peelDialogCopy} from "./peelDialogCopy";
import {hangingTagReserve} from "./hangingTagReserve";
import {useIsomorphicLayoutEffect} from "utils/useIsomorphicLayoutEffect";
import {BottomRow, CodeConfirmation, CodeFace, CodeNumber, CodeTurn, CodeUrl, ConfettiCanvas, Content, DialogTag, Dismiss, Frame, GiftGlyph, Heading, PEEL_COMPACT_QUERY, Pull, Pulls, Scrim, TAG_RETREAT_MS, TagAction, TagBody, TagPosition, TagRow} from "./PeelTag.styles";

/** One card, one active candidate. Dismissal never changes the family's eligibility. */
export function PeelTagSlot({candidates, children, pinned = false}: {
	candidates: readonly PeelCandidate<ReactNode>[];
	children?: ReactNode;
	pinned?: boolean;
}) {
	const messages = useMessages();
	const {common} = messages;
	const [dismissed, setDismissed] = useState<string[]>([]);
	const [focusId, setFocusId] = useState<string | null>(null);
	// The tag being tucked. It stays mounted, riding its string back up behind the card (the
	// entrance in reverse, owner 2026-09-09), and is dismissed when that animation ends. Under
	// reduced motion the tuck is instant: the retreat rule is suppressed and would never end.
	const [leavingId, setLeavingId] = useState<string | null>(null);
	const reduceMotion = useReducedMotion() !== false;
	const root = useRef<HTMLDivElement>(null);
	// Settled at mount, like the rest of the tag's geometry: the success card exists only after a send,
	// so there is no pre-rendered frame to disagree with, and a viewport resized mid-screen re-reads on
	// the next send rather than swapping a tag's placement mid-animation.
	const [compact] = useState(() => typeof window !== "undefined" && typeof window.matchMedia === "function"
		&& window.matchMedia(PEEL_COMPACT_QUERY).matches);
	// The tag the reader opened from its tab; see `peelTagCollapsed` for why a compact layout waits.
	const [openedId, setOpenedId] = useState<string | null>(null);
	// The tag whose dialog is open, for a tag that opens in one instead of over a compact card (ui#3343).
	const [dialogId, setDialogId] = useState<string | null>(null);
	const selected = resolvePeelTag(candidates, dismissed);
	const collapsed = peelTagCollapsed(selected, compact, openedId);
	const inDialog = peelTagOpensInDialog(selected, compact);
	const dialogCopy = inDialog && selected?.dialog !== undefined ? peelDialogCopy(messages, selected.dialog) : null;
	// A dialog belongs to the tag that opened it: a tag that loses the slot and later wins it back
	// (a campaign outranking the coupon for a while) returns as its tab, never as an open dialog.
	useEffect(() => setDialogId(previous => previous === selected?.id ? previous : null), [selected?.id]);
	// The pull is there from the first frame of the retreat, so the frame's height settles on the
	// pull's row as the tag's row shrinks and nothing moves when the tag finally unmounts.
	const pullId = visiblePull(candidates, leavingId === null ? dismissed : setPeelTagDismissed(dismissed, leavingId, true));
	const pullCandidate = candidates.find(candidate => candidate.id === pullId);
	const pullIsTicket = pullCandidate?.appearance === "ticket";
	// The pull's row mounts closed and opens on the next frame — but never while a tag is still
	// retreating: with both rows animating at once their heights briefly SUM past the settled
	// height, and inside the height-capped frame the card (the one shrinkable item) absorbed the
	// excess and visibly lost its bottom edge for the length of the retreat (owner 2026-09-10).
	// `leavingId` clearing in finishTuck is what re-runs this and opens the row.
	const [pullOpen, setPullOpen] = useState(false);
	useEffect(() => {
		if (pullId === null) { setPullOpen(false); return; }
		if (leavingId !== null) return;
		const frame = requestAnimationFrame(() => setPullOpen(true));
		return () => cancelAnimationFrame(frame);
	}, [pullId, leavingId]);
	// The pull drops in only after a retreat; a pull that is there on arrival (a reduced-motion tuck,
	// a reopened-then-tucked tag on a pinned slot) stays still.
	const [pullFalling, setPullFalling] = useState(false);
	const finishTuck = (id: string) => {
		setPullFalling(leavingId === id);
		setLeavingId(null);
		setDismissed(previous => setPeelTagDismissed(previous, id, true));
		setFocusId(`pull-${id}`);
	};
	useEffect(() => {
		if (leavingId === null) return;
		// `animationend` is the primary signal; the timer covers a tag whose animation never runs
		// (a hidden tab, a display:none ancestor) so a tuck can never wedge the slot open.
		const fallback = window.setTimeout(() => finishTuck(leavingId), TAG_RETREAT_MS + 120);
		return () => window.clearTimeout(fallback);
	}, [leavingId]);
	/**
	 * What hangs below the card, published on the frame as `--peel-hanging-reserve` for the card's own
	 * viewport cap to subtract (kitecorp/transferify-ui#2485).
	 *
	 * A card sized as if nothing hung under it pushes the tag past the fold, and the browser stops
	 * hit-testing there: the invite tag's action ended exactly at the viewport's bottom edge with the
	 * lower half of its touch target outside. The rule is {@link hangingTagReserve}; this adapter only
	 * measures and publishes.
	 *
	 * Measured after the fonts settle and again on resize, and NOT through a ResizeObserver: the row's
	 * height animates through every tuck, and a cap that tracked it would resize the card on each frame
	 * of an animation whose whole point is that the card stays still.
	 */
	useIsomorphicLayoutEffect(() => {
		const frame = root.current;
		if (frame === null) return;
		let live = true;
		const publish = () => {
			if (!live) return;
			const row = frame.querySelector<HTMLElement>("[data-peel-position]");
			const wrapper = row?.closest<HTMLElement>("[data-peel-row]");
			const reserve = row === null ? 0 : hangingTagReserve({
				rowHeight: row.getBoundingClientRect().height,
				gapAbove: Number.parseFloat(getComputedStyle(row).marginTop) || 0,
				outOfFlow: wrapper ? getComputedStyle(wrapper).position === "absolute" : false,
			});
			frame.style.setProperty("--peel-hanging-reserve", `${reserve}px`);
		};
		publish();
		void document.fonts?.ready.then(publish).catch(() => {});
		window.addEventListener("resize", publish);
		return () => { live = false; window.removeEventListener("resize", publish); };
	}, [selected?.id, pullId, pullOpen, leavingId, collapsed]);

	useEffect(() => {
		if (focusId !== null) {
			// Dismissing can reveal another tag instead of a pull. Focus its surviving control.
			const target = root.current?.querySelector<HTMLElement>(`[data-peel-focus="${CSS.escape(focusId)}"]`)
				?? root.current?.querySelector<HTMLElement>("[data-peel-focus]");
			target?.focus();
			setFocusId(null);
		}
	}, [focusId]);
	const tuck = (tag: PeelCandidate<ReactNode>) => {
		tag.onDismiss?.();
		if (reduceMotion || pinned) finishTuck(tag.id);
		else {
			// A second tuck must not inherit the first one's drop: with the flag still set the
			// pull animated while hidden during the retreat and arrived already still.
			setPullFalling(false);
			setLeavingId(tag.id);
		}
	};
	// The card lifts as a tag drops from it or slides out from behind it; a tag opening OVER a compact
	// card lifts nothing, since the reader is looking at the tag, not at the card under it, and neither
	// does a tag that opens in a dialog, since nothing leaves the card at all.
	const cardLifts = selected !== null && !selected.bare && !collapsed && !inDialog && !(compact && selected.placement === "beside");
	return (
		<Frame ref={root} $pinned={pinned} $entrance={cardLifts} data-peel-frame="" data-peel-pinned={pinned || undefined}>
			{children !== undefined && <div data-peel-card="">{children}</div>}
			{/* The dim over a compact card while its ticket is open over it (ui#2949). Pointer-only, as
			  * every scrim here is: the ticket's own × is the keyboard's way out. */}
			{selected !== null && peelScrimShown(selected, compact, collapsed, leavingId !== null) && <Scrim aria-hidden="true" data-peel-scrim=""
				onClick={() => {
					if (peelScrimTucks(selected)) tuck(selected);
					else { setOpenedId(null); setFocusId(`pull-${selected.id}`); }
				}}/>}
			{selected?.bare && <div key={selected.id} data-peel-action-position="" data-peel-family={selected.family}>{selected.node}</div>}
			{/* On a compact layout a tag that opens in a dialog (ui#3343, ui#3372) stays its tab on the card's
			  * left edge, clear of the title and the link box, and opens in the app's own dialog rather than
			  * over the card or under it: Escape, the scrim and its ✕ close it, and MUI hands focus back to the
			  * tab, which stays mounted under it. Closing reports the tag dismissed, as tucking does. A ticket
			  * prints its own paper; a gift tag's paper is the slot's, so the dialog lays the same paper under it. */}
			{dialogCopy !== null && selected !== null && <>
				{wrapRow("pull", pinned, true, true, <Pulls data-peel-pulls="" $beside>
					<Pull key={selected.id} type="button" $beside $ticket={selected.appearance === "ticket"} aria-haspopup="dialog"
						aria-label={dialogCopy.openLabel} data-peel-focus={`pull-${selected.id}`} data-peel-trigger=""
						onClick={() => setDialogId(selected.id)}>
						{selected.appearance === "ticket" ? <CouponIcon size={22}/> : <GiftIcon size={22}/>}
					</Pull>
				</Pulls>)}
				<ClosableDialog title={dialogCopy.title} open={dialogId === selected.id} narrowGutter onClose={() => {
					setDialogId(null);
					selected.onDismiss?.();
				}}><DialogTag>{selected.appearance === "ticket" ? selected.node
					: <TagBody data-peel-family={selected.family}>{selected.node}</TagBody>}</DialogTag></ClosableDialog>
			</>}
			{selected !== null && !selected.bare && !collapsed && !inDialog && wrapRow("tag", pinned, leavingId !== selected.id, selected.placement === "beside",
				<TagPosition key={selected.id} $pinned={pinned} $entrance={!pinned} $ticket={selected.appearance === "ticket"} $leaving={leavingId === selected.id}
					$beside={selected.placement === "beside"} data-peel-position="" data-peel-leaving={leavingId === selected.id || undefined}
					onAnimationEnd={event => { if (event.target === event.currentTarget && leavingId === selected.id) finishTuck(selected.id); }}>
					{pinned ? selected.node : <TagBody $ticket={selected.appearance === "ticket"} data-testid="peel-tag" data-peel-family={selected.family}>
						{selected.node}
						<Dismiss type="button" $ticket={selected.appearance === "ticket"} aria-label={selected.dismissLabel ?? common.tuckTag} data-peel-focus={`dismiss-${selected.id}`} disabled={leavingId !== null} onClick={() => tuck(selected)}>
							<svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true"><path d="m4 4 8 8m0-8-8 8" stroke="currentColor" strokeWidth="1.8"/></svg>
						</Dismiss>
					</TagBody>}
				</TagPosition>,
			)}
			{/* A collapsed tag's tab opens it for the first time; the pull after it reopens a tucked one. */}
			{collapsed && selected !== null && wrapRow("pull", pinned, true, true, <Pulls data-peel-pulls="" $beside>
				<Pull key={selected.id} type="button" $beside $ticket={selected.appearance === "ticket"} aria-label={common.openTag} data-peel-focus={`pull-${selected.id}`} data-peel-opens="" onClick={() => {
					setOpenedId(selected.id);
					setFocusId(`dismiss-${selected.id}`);
				}}>{selected.appearance === "ticket" ? <CouponIcon size={22}/> : <GiftIcon size={22}/>}</Pull>
			</Pulls>)}
			{!collapsed && pullId !== null && wrapRow("pull", pinned, pullOpen || reduceMotion, pullCandidate?.placement === "beside", <Pulls data-peel-pulls="" $beside={pullCandidate?.placement === "beside"} $hidden={leavingId !== null} $falling={pullFalling && !reduceMotion}>
				<Pull key={pullId} type="button" $beside={pullCandidate?.placement === "beside"} $ticket={pullIsTicket} aria-label={common.reopenTag} data-peel-focus={`pull-${pullId}`} onClick={() => {
					setDismissed(previous => setPeelTagDismissed(previous, pullId, false));
					setFocusId(`dismiss-${pullId}`);
				}}>{pullIsTicket ? <CouponIcon size={22}/> : <GiftIcon size={22}/>}</Pull>
			</Pulls>)}
		</Frame>
	);
}

/**
 * A pinned tag is absolutely positioned and needs no row; a dropped tag and its pull each get one that
 * opens and closes. The key matters: without it React reused the closed tag row as the pull row when
 * the tag unmounted, and the pull's row re-ran its opening transition from zero (a visible flicker).
 *
 * `beside` marks a row the frame lays out beside the card, or over it on a compact layout (ui#2949),
 * instead of under it; the frame's styles own where that is.
 */
function wrapRow(key: "tag" | "pull", pinned: boolean, open: boolean, beside: boolean, content: ReactNode) {
	return pinned ? content : <TagRow key={key} $open={open} data-peel-row="" data-peel-beside={beside ? key : undefined}><div>{content}</div></TagRow>;
}

/** Shared gift face; callers retain their own copy, clipboard and offer handlers. */
export function PeelTag({title, children, action, copied = false}: {
	title: string; children?: ReactNode; action?: ReactNode; copied?: boolean;
}) {
	const headingId = useId();
	return (
		<div role="group" aria-labelledby={headingId}>
			<Heading id={headingId}>{title}</Heading>
			<Content>{children}</Content>
			<BottomRow><GiftGlyph aria-hidden="true"><GiftIcon size={22}/></GiftGlyph>{action}</BottomRow>
			<PeelConfetti active={copied}/>
		</div>
	);
}

/** The same burst celebrates invite copying and ticket claiming. */
export function PeelConfetti({active: copied}: {active: boolean}) {
	const canvas = useRef<HTMLCanvasElement>(null);
	const {tokens} = useTheme();
	useEffect(() => {
		if (!copied || !canvas.current || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
		const surface = canvas.current;
		const context = surface.getContext("2d");
		if (!context) return;
		const colours = [tokens.gold, tokens.goldDark, tokens.goldInk, tokens.goldEdge];
		const bits = Array.from({length: 46}, (_, index) => ({
			x: 210 + (Math.random() - .5) * 60, y: 110,
			vx: (Math.random() - .5) * 7, vy: -6 - Math.random() * 6,
			r: 3 + Math.random() * 4, a: Math.random() * Math.PI,
			va: (Math.random() - .5) * .3, colour: colours[index % colours.length],
		}));
		let frame = 0;
		let request = 0;
		const burstConfetti = () => {
			context.clearRect(0, 0, 420, 260);
			for (const bit of bits) {
				bit.vy += .28; bit.x += bit.vx; bit.y += bit.vy; bit.a += bit.va;
				context.save(); context.globalAlpha = Math.max(0, 1 - frame * .014);
				context.translate(bit.x, bit.y); context.rotate(bit.a); context.fillStyle = bit.colour;
				context.fillRect(-bit.r, -bit.r / 2, bit.r * 2, bit.r); context.restore();
			}
			if (++frame < 80) request = requestAnimationFrame(burstConfetti);
			else context.clearRect(0, 0, 420, 260);
		};
		request = requestAnimationFrame(burstConfetti);
		return () => { cancelAnimationFrame(request); context.clearRect(0, 0, 420, 260); };
	}, [copied, tokens]);
	return <ConfettiCanvas ref={canvas} width={420} height={260} aria-hidden="true"/>;
}

/**
 * Compact account variant. Other gift surfaces deliberately keep their non-flipping face.
 *
 * <b>The copy control is HANDED in, not built here</b> (kitecorp/transferify-api-service#1582). The
 * flip is this tag's business; what a copy MEANS is the surface's — on the referral card it is an
 * invite handed out and belongs on the funnel, and a tag in `peel/` must not know that. Every caller
 * passes `referral/useInviteShare`, which is `useCopyFeedback` plus that one report.
 */
export function PeelCodeTag({code, url, label, share}: {
	code: string; url: string; label: string; share: CopyControl;
}) {
	const {common} = useMessages();
	const reduceMotion = useReducedMotion() !== false;
	const {copied, copy} = share;
	const [face, setFace] = useState<PeelTagFace>("front");
	const back = face === "back" && !reduceMotion;
	const copyButton = useRef<HTMLButtonElement>(null);
	const doneButton = useRef<HTMLButtonElement>(null);
	const previouslyBack = useRef(false);
	useEffect(() => {
		if (previouslyBack.current !== back) (back ? doneButton : copyButton).current?.focus({preventScroll: true});
		previouslyBack.current = back;
	}, [back]);
	useEffect(() => { if (reduceMotion) setFace("front"); }, [reduceMotion]);
	const copyAndFlip = async () => setFace(peelFaceAfterCopy(await copy(), reduceMotion));
	return <CodeTurn $back={back} data-testid="peel-tag" data-peel-family="gift" data-face={back ? "back" : "front"}>
		<CodeFace data-testid="peel-code-front" aria-hidden={back} inert={back}>
			{/*
			  * The label names the CODE, and only the code (ui#1234). It used to sit on the flipping
			  * wrapper, which spans both faces: assistive technology discarded it there, while
			  * `getByLabel` (which reads the raw attribute, never the computed name) matched that whole
			  * card and handed four e2e specs "CODE / Copy link / Copied. / <url> / Done" where they
			  * expected eight characters (ui#2295).
			  *
			  * `role="group"` because the value is a `code` element, and `code` is a
			  * name-from-author-PROHIBITED role: the same defect ui#1223 removed from the download
			  * page's chip. A role that permits a name is what carries the attribute into the
			  * accessibility tree, so the announced name is the label and the announced content is the
			  * code itself.
			  */}
			<CodeNumber role="group" aria-label={label}>{code}</CodeNumber>
			<TagAction ref={copyButton} type="button" onClick={copyAndFlip}>{copied ? common.copied : common.copyLink}</TagAction>
		</CodeFace>
		<CodeFace $reverse data-testid="peel-code-back" aria-hidden={!back} inert={!back}>
			<CodeConfirmation>{common.copiedTitle}</CodeConfirmation>
			<CodeUrl>{url}</CodeUrl>
			<TagAction ref={doneButton} type="button" onClick={() => setFace("front")}>{common.done}</TagAction>
		</CodeFace>
	</CodeTurn>;
}
